"use client";
import React from "react";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import Image from "next/image";
import { GroqImage } from "@/helpers/Image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import NavBarV2 from "@/components/global/partials/HeaderV2";
import Footer from "@/components/global/partials/Footer";
import HeroSection from "../pageComponents/challenge/Hero";
import DealSection from "../pageComponents/challenge/Deal";
import bg from "@/public/home/section-bg.jpg";
import ForWhoSection from "../pageComponents/challenge/Who";
import Process from "../pageComponents/challenge/Process";
import Reviews from "../pageComponents/challenge/Reviews";
import About from "../pageComponents/challenge/About";
import StepsSection from "../pageComponents/challenge/Steps";
import FaqSection from "../pageComponents/challenge/FAQ";
import LastCTASection from "../global/sections/LastCTA";
import ReviewsSection from "../pageComponents/challenge/Reviews";
import { Motion } from "../global/utils/Motion";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import logo from "@/public/logo-black.svg";
import Link from "next/link";
import { motion } from "framer-motion";

export default function PageChallengeJSX({
  data,
}: {
  data: PageChallengeSchema;
}) {
  const pathname = usePathname();
  const lang =
    pathname.slice(-3) === "/de" || pathname === "/de"
      ? "de"
      : pathname.slice(-3) === "/br" || pathname === "/br"
        ? "br"
        : "en";
  return (
    <>
      <nav
        className={cn(
          "flex absolute z-40 justify-between items-end w-full lg:px-0 top-[4rem] start-0 px-[4rem] lg:top-[9rem]",
        )}
      >
        <div className="flex relative z-[3] justify-between items-center mx-auto w-full max-w-[187.375rem]">
          <div
            className={`w-full py-[1.5rem] lg:py-0 mx-auto flex justify-center`}
          >
            <Link
              className=" relative z-[2] flex w-fit"
              href={lang === "de" ? "/de" : lang === "br" ? "/br" : "/"}
            >
              <Image alt="Logo" src={logo} className="w-[18.375rem]" />
            </Link>
          </div>
        </div>
      </nav>

      <HeroSection data={data} />
      <DealSection data={data} />
      <div
        className="relative px-[2rem] lg:px-[5rem] w-full mb-[2.5rem] lg:mb-[5rem]"
        style={{ overflowAnchor: "none" }}
      >
        <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem]">
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none bg-[#E6EDE9]">
            <Image
              className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,120rem)] !max-w-none !h-auto object-cover mix-blend-luminosity opacity-[.10]"
              style={{
                maskImage:
                  "linear-gradient(to top, black 20%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to top, black 20%, transparent 100%)",
              }}
              src={bg}
              quality={100}
              alt="background art"
            />
          </div>
          <ForWhoSection data={data} />
        </div>
      </div>
      <div
        className="relative px-[2rem] lg:px-[5rem] w-full"
        style={{ overflowAnchor: "none" }}
      >
        <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem]">
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none bg-[#103037]">
            <Image
              className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,120rem)] !max-w-none !h-auto object-cover mix-blend-luminosity opacity-[.10]"
              style={{
                maskImage:
                  "linear-gradient(to top, black 20%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to top, black 20%, transparent 100%)",
              }}
              src={bg}
              quality={100}
              alt="background art"
            />
          </div>
          <Process data={data} />
        </div>
      </div>

      {data.reviewsTitle && data.reviewsDocument && (
        <ReviewsSection
          reviewsTitle={data.reviewsTitle}
          reviewsDocument={data.reviewsDocument}
          slug={data?.settings?.slug?.current}
        />
      )}

      <div
        className="relative px-[2rem] lg:px-[5rem] w-full"
        style={{ overflowAnchor: "none" }}
      >
        <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem]">
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none bg-[#E6EDE9]">
            <Image
              className="absolute left-1/2 -translate-x-1/2 top-0 -scale-100 !w-[max(100%,120rem)] !max-w-none !h-auto object-cover mix-blend-luminosity opacity-[.10]"
              style={{
                maskImage:
                  "linear-gradient(to top, black 20%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to top, black 20%, transparent 100%)",
              }}
              src={bg}
              quality={100}
              alt="background art"
            />
          </div>
          <About data={data} />
          <StepsSection
            chip={data.stepsChip}
            title={data.stepsTitle}
            steps={data.steps}
            cta={data.stepsCTA}
          />
        </div>
      </div>

      <FaqSection
        chip={data.faqChip}
        title={data.faqTitle}
        faqList={data.faqList}
      />

      <LastCTASection
        chip={data?.lastChip}
        text={data?.lastText}
        cta={data?.lastCTA}
      />
      {data?.settings?.footer && (
        <Footer
          data={data.settings.footer}
          slug={data?.settings?.slug?.current}
        />
      )}
    </>
  );
}
